import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_LIBRARY_FILTER_STATE,
  clearedLibraryFilterState,
  libraryFilterStateToParams,
  parseLibraryFilterState,
  type LibraryFilterAxis,
  type LibraryFilterState,
  type LibraryGroupMode,
  type LibrarySort,
  type LibraryTypeFilter,
  type LibraryUsedFilter,
} from './libraryFilterState.ts';

export type LibraryFilterActions = {
  patch: (next: Partial<LibraryFilterState>) => void;
  setQuery: (query: string) => void;
  setType: (type: LibraryTypeFilter) => void;
  setCategory: (category: string | null) => void;
  setUsed: (used: LibraryUsedFilter) => void;
  setGroup: (group: LibraryGroupMode) => void;
  setSort: (sort: LibrarySort) => void;
  /** Clear one axis from its chip. */
  clearAxis: (axis: LibraryFilterAxis) => void;
  /** Drop every filter, keep grouping and sort. */
  clearFilters: () => void;
};

/**
 * The Library's view state, mirrored into the URL.
 *
 * The wiring half of `libraryFilterState.ts`, written the same way as
 * `useDeckFilterState`: hydrated straight from the URL on first render rather
 * than in an effect, so a shared link's view is the first thing painted, and
 * written back with `replace` so filtering does not fill the back button per
 * keystroke.
 *
 * `enabled` is the one thing the decks page has no need of. It is false on the
 * BANK tab, and it switches off BOTH halves — the hydrate and the mirror.
 * `/library/bank` is `BankLibrary`'s surface and it owns `?category=` there; a
 * Slides-mode state that carried on writing params behind it would fight it for
 * the address bar, and would also hand this tab a category picked on the other
 * one. It is the same guard the page's old two-effect version carried, in one
 * place instead of two.
 */
export function useLibraryFilterState(enabled: boolean): { filters: LibraryFilterState } & LibraryFilterActions {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<LibraryFilterState>(() =>
    enabled ? parseLibraryFilterState(searchParams) : DEFAULT_LIBRARY_FILTER_STATE,
  );

  // State is the source of truth from here on; the URL follows it. Compared as
  // strings first because React Router hands back a fresh object each render
  // and setting an identical query string would loop.
  useEffect(() => {
    if (!enabled) return;
    const next = libraryFilterStateToParams(filters, searchParams);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, enabled]);

  const patch = useCallback((next: Partial<LibraryFilterState>) => {
    setFilters((cur) => {
      const merged = { ...cur, ...next };
      // Cheap identity guard: a setter called with the value already held
      // shouldn't re-run every consumer's derivation.
      const same = (Object.keys(merged) as (keyof LibraryFilterState)[]).every((k) => merged[k] === cur[k]);
      return same ? cur : merged;
    });
  }, []);

  return {
    filters,
    patch,
    setQuery: useCallback((query: string) => patch({ query }), [patch]),
    setType: useCallback((type: LibraryTypeFilter) => patch({ type }), [patch]),
    setCategory: useCallback((category: string | null) => patch({ category }), [patch]),
    setUsed: useCallback((used: LibraryUsedFilter) => patch({ used }), [patch]),
    setGroup: useCallback((group: LibraryGroupMode) => patch({ group }), [patch]),
    setSort: useCallback((sort: LibrarySort) => patch({ sort }), [patch]),
    clearAxis: useCallback(
      (axis: LibraryFilterAxis) => patch({ [axis]: DEFAULT_LIBRARY_FILTER_STATE[axis] } as Partial<LibraryFilterState>),
      [patch],
    ),
    clearFilters: useCallback(() => setFilters((cur) => clearedLibraryFilterState(cur)), []),
  };
}
